import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Plus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { queryClient } from '~app/query-client';

import {
  getSingleStudentChargeOutcome,
  useBulkChargeStudents,
} from '~entities/payment/api/bulk-charge-students';
import {
  formatEnrollmentSavedMethodLabel,
  pickEnrollmentCatalogDefaultMethod,
  useGetEnrollmentPaymentMethods,
} from '~entities/payment/api/get-enrollment-payment-methods';
import { useGetTransactionCode } from '~entities/payment/api/get-transaction-codes';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';

import { KEYS } from '~shared/api/query-keys';
import { DateTimePicker } from '~shared/components/date-time-picker';
import { Button } from '~shared/shadcn/ui/button';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~shared/shadcn/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~shared/shadcn/ui/form';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import { Switch } from '~shared/shadcn/ui/switch';
import {
  formatScheduledDate,
  getEarliestScheduledDay,
  isScheduledDateSelectable,
  parseScheduledDate,
} from '~shared/utils/scheduled-date';

// Custom refinement for validating decimal places
const validateDecimalPlaces = (value: number) => {
  // Convert to string and check decimal places
  const stringValue = value.toString();
  const parts = stringValue.split('.');

  // If there's a decimal part and it has more than 2 digits
  if (parts.length > 1) {
    const decimalPart = parts[1];
    if (decimalPart && decimalPart.length > 2) {
      return false;
    }
  }

  return true;
};

const formSchema = z
  .object({
    chargeType: z.string().min(1, { message: 'Charge name is required' }),
    amount: z.coerce
      .number()
      .min(1, { message: 'Amount must be at least 1' })
      .refine(validateDecimalPlaces, {
        message: 'Amount can have at most 2 decimal places',
      }),
    transactionCodeId: z.string().optional(),
    schedulePayment: z.boolean().default(false),
    scheduledDate: z.string().nullable().optional(),
    skipPaymentMethod: z.boolean().default(false),
  })
  .refine(
    (data) => {
      if (data.schedulePayment && !data.scheduledDate) {
        return false;
      }
      if (
        data.schedulePayment &&
        data.scheduledDate &&
        !isScheduledDateSelectable(data.scheduledDate)
      ) {
        return false;
      }
      return true;
    },
    {
      message: 'Scheduled date must be in the future',
      path: ['scheduledDate'],
    },
  );

type FormValues = z.infer<typeof formSchema>;

export const ManuallyCharge = ({
  studentId,
  parentId,
}: {
  studentId: string;
  parentId: string;
}) => {
  const [open, setOpen] = useState(false);
  const [resolvedPaymentMethodId, setResolvedPaymentMethodId] = useState<
    string | undefined
  >(undefined);
  const bulkCharge = useBulkChargeStudents();
  const { data: studioDetails } = useGetStudioDetails();
  const isV2 = studioDetails?.isV2 === true;
  const { data: transactionCodesData } = useGetTransactionCode();
  const transactionCodes = transactionCodesData ?? [];

  const { data: enrollmentPaymentMethods, isLoading: isLoadingEnrollmentPms } =
    useGetEnrollmentPaymentMethods({
      parentId,
      enabled: isV2 && open && !!parentId,
    });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      chargeType: '',
      amount: 1,
      transactionCodeId: '',
      schedulePayment: false,
      scheduledDate: null,
      skipPaymentMethod: false,
    },
  });

  const schedulePayment = form.watch('schedulePayment');
  const skipPaymentMethod = form.watch('skipPaymentMethod');

  // Match enroll-student: default to catalog default; keep selection if still valid.
  useEffect(() => {
    if (!isV2 || skipPaymentMethod) return;

    const catalog = enrollmentPaymentMethods;
    if (!catalog) return;

    const selectedStillValid =
      !!resolvedPaymentMethodId &&
      catalog.savedMethods.some(
        (method) => method.id === resolvedPaymentMethodId,
      );
    if (selectedStillValid) return;

    const defaultMethod = pickEnrollmentCatalogDefaultMethod(catalog);
    setResolvedPaymentMethodId(defaultMethod?.id);
  }, [
    isV2,
    skipPaymentMethod,
    enrollmentPaymentMethods,
    resolvedPaymentMethodId,
  ]);

  const handleSubmit = (values: FormValues) => {
    // Format amount to 2 decimal places
    const formattedAmount = parseFloat(values.amount.toFixed(2));
    const skipPm = !values.schedulePayment && values.skipPaymentMethod;

    if (isV2 && !skipPm && !resolvedPaymentMethodId?.trim()) {
      toast.error(
        'Please select a saved payment method, or check "No immediate charge".',
      );
      return;
    }

    bulkCharge.mutate(
      {
        studentId: [studentId],
        amount: formattedAmount.toString(),
        chargeName: values.chargeType,
        isSingleStudent: true,
        ...(values.transactionCodeId && {
          transactionCodeId: values.transactionCodeId,
        }),
        schedulePayment: values.schedulePayment,
        scheduledDate: values.schedulePayment ? values.scheduledDate : null,
        skipPaymentMethod: skipPm,
        ...(isV2 && !skipPm && resolvedPaymentMethodId
          ? { paymentMethodId: resolvedPaymentMethodId }
          : {}),
        isV2,
      },
      {
        onSuccess: (data) => {
          const outcome = getSingleStudentChargeOutcome(data, studentId);

          if (outcome.type === 'error') {
            toast.error(outcome.message);
            return;
          }

          toast.success(outcome.message);
          setOpen(false);
          form.reset();
          setResolvedPaymentMethodId(undefined);
          setTimeout(() => {
            void queryClient.invalidateQueries({
              predicate(query) {
                return query.queryKey[0] === KEYS.STUDENT_PAYMENT_HISTORY;
              },
            });
          }, 1000);
          setTimeout(() => {
            void queryClient.invalidateQueries({
              predicate(query) {
                return query.queryKey[0] === KEYS.STUDENT_PAYMENT_HISTORY;
              },
            });
          }, 3000);
        },
        onError: () => {
          toast.error('Failed to submit charge. Please try again.');
        },
      },
    );
  };

  const handleToggle = (openState: boolean) => {
    if (!openState) {
      form.reset();
      setResolvedPaymentMethodId(undefined);
    }
    setOpen(openState);
  };

  const clearTransactionCode = () => {
    form.setValue('transactionCodeId', '', { shouldValidate: true });
  };

  // Memoize the earliest schedulable day to avoid recreating on every render
  const earliestScheduledDay = useMemo(() => getEarliestScheduledDay(), []);

  const savedMethods = enrollmentPaymentMethods?.savedMethods ?? [];
  const showPaymentMethodPicker = isV2 && !skipPaymentMethod;

  return (
    <Dialog open={open} onOpenChange={handleToggle}>
      <DialogTrigger asChild>
        <Button variant='outline' className='w-full'>
          <Plus />
          Manually Charge
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Manually Charge Student</DialogTitle>
          <DialogDescription>
            Create a manual charge for this student.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className='space-y-4 py-4'
          >
            <FormField
              control={form.control}
              name='chargeType'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Charge Name</FormLabel>
                  <FormControl>
                    <Input placeholder='Enter charge type' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='transactionCodeId'
              render={({ field }) => (
                <FormItem>
                  <div className='flex items-center justify-between'>
                    <FormLabel>Transaction Code</FormLabel>
                    {field.value && (
                      <Button
                        type='button'
                        variant='ghost'
                        size='sm'
                        className='h-6 px-2 text-muted-foreground'
                        onClick={clearTransactionCode}
                      >
                        <X className='size-3' />
                        <span className='sr-only'>Clear</span>
                      </Button>
                    )}
                  </div>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder='Select Transaction Code' />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Select Transaction Code</SelectLabel>
                        {transactionCodes.map((transactionCode) => (
                          <SelectItem
                            key={transactionCode._id}
                            value={transactionCode._id}
                          >
                            {transactionCode.code}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='amount'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount</FormLabel>
                  <FormControl>
                    <Input
                      type='number'
                      min='1'
                      step='0.01'
                      placeholder='Enter amount (min: 1)'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='schedulePayment'
              render={({ field }) => (
                <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
                  <div className='space-y-0.5'>
                    <FormLabel className='text-base'>
                      Schedule the payment
                    </FormLabel>
                    <div className='text-sm text-muted-foreground'>
                      Enable to schedule this payment for a future date
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                        if (checked) {
                          form.setValue('skipPaymentMethod', false);
                          if (isV2 && !resolvedPaymentMethodId) {
                            const method = pickEnrollmentCatalogDefaultMethod(
                              enrollmentPaymentMethods,
                            );
                            if (method) {
                              setResolvedPaymentMethodId(method.id);
                            }
                          }
                        }
                      }}
                      disabled={bulkCharge.isPending}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {schedulePayment && (
              <FormField
                control={form.control}
                name='scheduledDate'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Scheduled Date</FormLabel>
                    <FormControl>
                      <DateTimePicker
                        hourCycle={12}
                        granularity='day'
                        displayFormat={{ hour12: 'PPP' }}
                        value={
                          field.value
                            ? (parseScheduledDate(field.value) ?? undefined)
                            : undefined
                        }
                        onChange={(date) => {
                          field.onChange(
                            date ? formatScheduledDate(date) : null,
                          );
                        }}
                        disabledDays={(date) => date < earliestScheduledDay}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {showPaymentMethodPicker && (
              <div className='grid gap-2'>
                <Label>Payment Method</Label>
                {isLoadingEnrollmentPms ? (
                  <p className='flex items-center gap-2 text-sm text-muted-foreground'>
                    <Loader2 className='size-3.5 animate-spin' />
                    Loading saved payment methods…
                  </p>
                ) : savedMethods.length > 0 ? (
                  <div className='space-y-2'>
                    <p className='text-sm font-medium text-muted-foreground'>
                      Saved payment methods
                    </p>
                    <Select
                      value={resolvedPaymentMethodId ?? ''}
                      onValueChange={(id) => {
                        const selected = savedMethods.find(
                          (method) => method.id === id,
                        );
                        if (!selected) return;
                        form.setValue('skipPaymentMethod', false);
                        setResolvedPaymentMethodId(selected.id);
                      }}
                      disabled={bulkCharge.isPending}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder='Select a saved payment method' />
                      </SelectTrigger>
                      <SelectContent>
                        {savedMethods.map((method) => (
                          <SelectItem key={method.id} value={method.id}>
                            {formatEnrollmentSavedMethodLabel(method)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <p className='text-sm text-muted-foreground'>
                    No saved payment methods for this parent. Save a card
                    first, or check &quot;No immediate charge&quot;.
                  </p>
                )}
              </div>
            )}

            {!schedulePayment && (
              <FormField
                control={form.control}
                name='skipPaymentMethod'
                render={({ field }) => (
                  <FormItem className='flex flex-row items-start space-x-3 space-y-0 rounded-lg border p-4'>
                    <FormControl>
                      <Checkbox
                        className='mt-0.5'
                        id='manual-charge-skip-payment-method'
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          if (checked === true) {
                            setResolvedPaymentMethodId(undefined);
                          } else if (isV2) {
                            const defaultMethod =
                              pickEnrollmentCatalogDefaultMethod(
                                enrollmentPaymentMethods,
                              );
                            if (defaultMethod) {
                              setResolvedPaymentMethodId(defaultMethod.id);
                            }
                          }
                          field.onChange(checked === true);
                        }}
                        disabled={bulkCharge.isPending}
                      />
                    </FormControl>
                    <div className='space-y-1 leading-none'>
                      <Label
                        htmlFor='manual-charge-skip-payment-method'
                        className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
                      >
                        No immediate charge
                      </Label>
                      <p className='text-sm font-normal leading-snug text-muted-foreground'>
                        Post the charge to the parent account without collecting
                        payment now. They can pay the outstanding balance later.
                      </p>
                    </div>
                  </FormItem>
                )}
              />
            )}

            {skipPaymentMethod && !schedulePayment && (
              <p className='text-sm text-muted-foreground'>
                Charge will be posted as an outstanding balance — no card will
                be charged at this time.
              </p>
            )}

            <DialogFooter>
              <Button
                variant='outline'
                type='button'
                onClick={() => {
                  setOpen(false);
                }}
                disabled={bulkCharge.isPending}
              >
                Cancel
              </Button>
              <Button
                type='submit'
                disabled={
                  bulkCharge.isPending ||
                  (showPaymentMethodPicker &&
                    (isLoadingEnrollmentPms || !resolvedPaymentMethodId))
                }
              >
                {bulkCharge.isPending && (
                  <Loader2 className='mr-2 size-4 animate-spin' />
                )}
                {bulkCharge.isPending
                  ? 'Processing...'
                  : skipPaymentMethod && !schedulePayment
                    ? 'Post Charge'
                    : 'Submit Charge'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
